export type EmailReason =
  | 'required'
  | 'single_at'
  | 'accent'
  | 'forbidden_char'
  | 'local_part'
  | 'domain'
  | 'extension'
  | 'length'
  | 'invalid_type'
  | 'format'

const LOCAL_PART = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+$/
const DOMAIN_LABEL = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/

export function normalizeEmail(email: string): string {
  return email.trim()
}

/**
 * Same local checks as the API EmailAddress helper. No DNS lookup.
 * Returns null when the address is acceptable.
 */
export function validateEmail(email: unknown): EmailReason | null {
  if (typeof email !== 'string') {
    return 'invalid_type'
  }

  const value = normalizeEmail(email)
  const structure = structureReason(value)
  if (structure !== null) {
    return structure
  }

  const at = value.indexOf('@')
  const local = value.slice(0, at)
  const domain = value.slice(at + 1)
  return localReason(local) ?? domainReason(domain)
}

/**
 * Translated reason for an address that must not be sent.
 * A mailto prefix is ignored. Blank input is reported as required.
 */
export function invalidEmailMessage(email: unknown, translate: (key: string) => string): string | null {
  if (typeof email !== 'string') {
    return translate('$vuetify.' + emailReasonLocaleKey('invalid_type'))
  }
  const address = normalizeEmail(email.replace(/^mailto:\s*/i, ''))
  if (address === '') {
    return translate('$vuetify.email_required')
  }
  const reason = validateEmail(address)
  if (reason === null) {
    return null
  }
  return translate('$vuetify.' + emailReasonLocaleKey(reason)) + ' (' + address + ')'
}

export function emailReasonLocaleKey(reason: string): string {
  switch (reason) {
    case 'required':
      return 'email_required'
    case 'single_at':
      return 'email_single_at'
    case 'accent':
      return 'email_accent'
    case 'forbidden_char':
      return 'email_forbidden_char'
    case 'local_part':
      return 'email_local_part'
    case 'domain':
    case 'format':
      return 'email_domain'
    case 'extension':
      return 'email_extension'
    case 'length':
      return 'email_length'
    default:
      return 'invalid_email'
  }
}

function hasNonAscii(email: string): boolean {
  for (let index = 0; index < email.length; index += 1) {
    if ((email.codePointAt(index) as number) > 127) {
      return true
    }
  }
  return false
}

function structureReason(email: string): EmailReason | null {
  if (email.length < 6 || email.length > 254) {
    return 'length'
  }
  if (hasNonAscii(email)) {
    return 'accent'
  }
  if ((email.match(/@/g) || []).length !== 1) {
    return 'single_at'
  }
  if (/[\s,;:<>()[\]{}]/.test(email)) {
    return 'forbidden_char'
  }
  return null
}

function localReason(local: string): EmailReason | null {
  if (local.length < 1 || local.length > 64) {
    return 'length'
  }
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..') || !LOCAL_PART.test(local)) {
    return 'local_part'
  }
  return null
}

function domainReason(domain: string): EmailReason | null {
  if (!domain.includes('.')) {
    return 'extension'
  }
  for (const label of domain.split('.')) {
    if (label.length < 1 || label.length > 63 || !DOMAIN_LABEL.test(label)) {
      return 'domain'
    }
  }
  return null
}

